import React, { useEffect, useState } from 'react';
import { Switch, Route, useLocation, useHistory } from 'react-router-dom';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { ThemeProvider } from 'styled-components';
import { lighten, darken, getContrast, getLuminance } from 'polished';

import Library from './mainCategory/Library';
import List from './mainCategory/List';
import { loadData, userInput } from './utils/helpers';
import { ILoadData } from './utils/interface';
import { Background, ListViewContrastButton } from './mainCategory/StyledComponents';

import { IdleModal } from './IdleModal';

import { skleraSDK } from '@sklera/sdk';
import { PublicViewContainer } from './publicView/Container';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToggleOn, faToggleOff, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

type AppConfigProps = {
    folder: string;
    fullscreenSwitch: boolean;
    highContrastSwitch: boolean;
    rows?: number;
    cols?: number;
    hideEmptyPlaylistSwitch: boolean;
    idleTimeoutSec?: number;
    secondColor: string;
    mainColor: string;
    borderColor?: string;
    backgroundColor?: string;
    font?: string;
    menuPosition?: 'top' | 'bottom' | 'center';
    isPublicApp?: boolean;
};
interface IAppContext {
    listScrollPosition: number;
    setListScrollPosition: (a: number) => void;
    highContrastMode: boolean;
}
interface IContextProps extends AppConfigProps, ILoadData, IAppContext {}

export const Context = React.createContext<Partial<IContextProps>>({});

function MunicipalNoticeBoard({
    loadedAppConfig,
    isPublicApp,
}: {
    loadedAppConfig: AppConfigProps;
    isPublicApp: boolean;
}) {
    const [appConfig, setAppConfig] = useState<AppConfigProps>(loadedAppConfig);
    const [folderData, setFolderData] = useState<ILoadData>();
    const [fontSize, setFontSize] = useState<string>('1vw');
    const [font, setFont] = useState<string>('');
    const [showIdleModal, setShowIdleModal] = useState(false);
    const [userInteractionSec, setUserInteractionSec] = useState(0);
    const [highContrastMode, setHighContrastMode] = useState<boolean>(false);
    const [listScrollPosition, setListScrollPosition] = useState<number>(0);

    const location = useLocation();
    const history = useHistory();

    const url = new URL(window.location.href);
    const sp = url.searchParams;

    isPublicApp = sp.has('forceApp') ? false : isPublicApp;

    useEffect(() => {
        document.body.style.overflow = isPublicApp ? 'inherit' : 'hidden';
    }, [isPublicApp]);

    useEffect(() => {
        const idleTimeoutSec = appConfig?.idleTimeoutSec;

        let timeout: number;

        if ((userInteractionSec || listScrollPosition) && idleTimeoutSec) {
            timeout = window.setTimeout(() => {
                if (location.pathname != '/' || listScrollPosition != 0) {
                    setShowIdleModal(true);
                }
            }, idleTimeoutSec * 1000);
        }
        return () => {
            timeout !== undefined && window.clearTimeout(timeout);
        };
    }, [userInteractionSec, location, listScrollPosition]);

    useEffect(() => {
        const userInteraction = () => {
            userInput();
            setUserInteractionSec((userInteractionSec) => {
                return userInteractionSec + 1;
            });
        };

        if (window.innerHeight > window.innerWidth) {
            setFontSize('1vh');
        } else {
            setFontSize('1vw');
        }

        window.addEventListener('click', () => {
            userInput();
            userInteraction();
        });
        const interval = skleraSDK.isPublicApp() ? setInterval(updateData, 30000) : undefined;
        updateData();

        document.body.addEventListener('click', userInteraction);

        return () => {
            document.body.removeEventListener('click', userInteraction);
            clearInterval(interval);
        };
    }, []);

    const updateData = async () => {
        try {
            const appConfig: AppConfigProps = await skleraSDK.loadConfig();
            if (appConfig && appConfig.folder) {
                const data = await loadData(appConfig.folder);
                setAppConfig(appConfig);
                setFolderData(data);
                if (appConfig.font) {
                    skleraSDK.loadFont(appConfig.font, 'myFont', (err: any, fontName: string) => {
                        if (fontName) setFont(fontName);
                        else console.log(err);
                    });
                }
            }
        } catch (e) {
            console.log('error', e);
        }
    };

    let contextValue: Partial<IContextProps> = {};
    if (folderData) {
        contextValue = folderData;
        contextValue.folder = appConfig?.folder;
        contextValue.fullscreenSwitch = appConfig?.fullscreenSwitch;
        contextValue.highContrastSwitch = appConfig?.highContrastSwitch;
        contextValue.rows = parseInt(appConfig?.rows + '') || 2;
        contextValue.cols = parseInt(appConfig?.cols + '') || 4;
        contextValue.hideEmptyPlaylistSwitch = appConfig?.hideEmptyPlaylistSwitch;
        contextValue.secondColor = appConfig?.secondColor || '#cccccc';
        contextValue.mainColor = appConfig?.mainColor || '#000000';
        contextValue.backgroundColor = appConfig?.backgroundColor;
        contextValue.idleTimeoutSec = appConfig?.idleTimeoutSec;
        contextValue.menuPosition = appConfig?.menuPosition;
        contextValue.isPublicApp = isPublicApp;
    }
    if (font) contextValue.font = font;

    const { mainColor = '#000000', secondColor = '#cccccc', borderColor, backgroundColor } = appConfig || {};

    const mainColorContrast = backgroundColor ? getContrast(mainColor, backgroundColor) : 10;
    const secondColorContrast = backgroundColor ? getContrast(secondColor, backgroundColor) : 10;

    const backgroundContrastColor =
        mainColorContrast < 2 && secondColorContrast > mainColorContrast ? secondColor : mainColor;

    function getShadowColor(color?: string) {
        if (!color) {
            return '#333333';
        }

        if (getLuminance(color) > 0.4) {
            return darken(0.2, color);
        }

        return lighten(0.2, color);
    }

    function resetSession(reset = true) {
        setShowIdleModal(false);
        if (reset) {
            setHighContrastMode(false);
            setListScrollPosition(0);
            history.push({ pathname: '/', search: location.search });
        }
    }

    const theme = {
        mainColor: highContrastMode ? 'white' : mainColor,
        secondColor: highContrastMode ? 'black' : secondColor,
        backgroundColor: highContrastMode ? 'black' : backgroundColor,
        backgroundContrastColor: highContrastMode ? 'black' : backgroundContrastColor,
        borderColor: highContrastMode ? 'white' : borderColor,

        mainColorLighter: lighten(0.4, highContrastMode ? 'white' : mainColor),
        secondColorLighter: lighten(0.4, highContrastMode ? 'black' : secondColor),

        mainColorDarker: darken(0.4, highContrastMode ? 'white' : mainColor),
        secondColorDarker: darken(0.4, highContrastMode ? 'black' : secondColor),

        mainColorShadow: getShadowColor(highContrastMode ? 'white' : mainColor),
        secondColorShadow: getShadowColor(highContrastMode ? 'black' : secondColor),
    };

    contextValue.listScrollPosition = listScrollPosition;
    contextValue.setListScrollPosition = (n) => {
        setListScrollPosition(n);
    };
    contextValue.highContrastMode = highContrastMode;

    return (
        <Context.Provider value={contextValue}>
            <ThemeProvider theme={theme}>
                {isPublicApp ? (
                    <PublicViewContainer />
                ) : (
                    <Background>
                        <TransitionGroup>
                            <CSSTransition timeout={350} classNames="fade" key={location.key}>
                                <div className="municipal-notice-board" style={{ filter: 'none', fontSize: fontSize }}>
                                    {appConfig.highContrastSwitch ? (
                                        <ListViewContrastButton
                                            onClick={() => setHighContrastMode((prevState) => !prevState)}
                                            style={font ? { fontFamily: font } : {}}>
                                            <FontAwesomeIcon
                                                icon={highContrastMode ? faToggleOn : faToggleOff}
                                                style={{ marginRight: '0.5em' }}
                                            />
                                            <FontAwesomeIcon icon={faEyeSlash} />
                                        </ListViewContrastButton>
                                    ) : null}
                                    <Switch location={location}>
                                        <Route exact path="/list">
                                            <List />
                                        </Route>
                                        <Route exact path="/">
                                            <Library />
                                        </Route>
                                    </Switch>
                                </div>
                            </CSSTransition>
                        </TransitionGroup>
                        <IdleModal showIdleModal={showIdleModal} resetSession={resetSession} />
                    </Background>
                )}
            </ThemeProvider>
        </Context.Provider>
    );
}

export default MunicipalNoticeBoard;
